/**
 * Spinner — circular loading indicator.
 *
 * TODO: This component belongs in @its-engineering-and-development/ui-foundation.
 *       Once added to the package, remove this local copy and import from there.
 *
 * @example
 * // Full-page loading state
 * if (isLoading) return <SpinnerPage />;
 *
 * // Inline with default size
 * <Spinner />
 *
 * // Custom size, color, track
 * <Spinner size={48} color="#120c80" trackColor="#eef0ff" strokeWidth={5} />
 *
 * TODO (future): replace hardcoded hex defaults with currentColor + CSS token
 *   so theme changes propagate automatically. Requires switching stroke to
 *   currentColor and setting color via className / CSS variable.
 */
import type { CSSProperties } from "react";
import "./spinner.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SpinnerProps {
  /** Diameter of the spinner in px. Default: 36 */
  size?: number;
  /** Color of the spinning arc. Accepts any CSS color. Default: #120c80 */
  color?: string;
  /** Color of the static background track. Default: #eef0ff */
  trackColor?: string;
  /** Stroke width in px. Default: 4 */
  strokeWidth?: number;
  /** Animation duration as a CSS time value. Default: "0.8s". Examples: "0.5s", "500ms" */
  duration?: string;
  /** Accessible label for screen readers. Default: "Loading" */
  label?: string;
  /** Extra class names for the wrapper element. */
  className?: string;
  /** Inline styles for the wrapper element. */
  style?: CSSProperties;
}

/** Circular spinning loader. Fully configurable via props. */
export function Spinner({
  size = 36,
  color = "#120c80",
  trackColor = "#eef0ff",
  strokeWidth = 4,
  duration = "0.8s",
  label = "Loading",
  className,
  style,
}: SpinnerProps) {
  const r = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * r;
  // Show ~¾ of the circle as the arc
  const dashArray = `${circumference * 0.75} ${circumference * 0.25}`;

  return (
    <svg
      role="status"
      aria-live="polite"
      aria-label={label}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      className={className}
      style={style}
    >
      {/* Static background track */}
      <circle
        cx={center}
        cy={center}
        r={r}
        stroke={trackColor}
        strokeWidth={strokeWidth}
      />
      {/* Spinning arc */}
      <circle
        cx={center}
        cy={center}
        r={r}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={dashArray}
        style={{
          transformOrigin: "center",
          animation: `spinner-rotate ${duration} linear infinite`,
        }}
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// SpinnerPage — full-section centered spinner for page-level loading states
// ---------------------------------------------------------------------------

/** Overrides Spinner defaults for page-level usage (larger, heavier stroke). */
const PAGE_SPINNER_DEFAULTS = {
  size: 48,
  strokeWidth: 5,
} as const;

export interface SpinnerPageProps extends SpinnerProps {
  /** Min height of the container. Default: "240px" */
  minHeight?: string;
  /**
   * Fixed height of the container.
   * When set, minHeight is ignored.
   */
  height?: string;
  /** Width of the container. Default: "100%" */
  width?: string;
}

/**
 * Centers a Spinner inside a section for page-level loading states.
 *
 * @example
 * if (isLoading) return <SpinnerPage />;
 * if (isLoading) return <SpinnerPage height="100dvh" />;
 */
export function SpinnerPage({
  size = PAGE_SPINNER_DEFAULTS.size,
  strokeWidth = PAGE_SPINNER_DEFAULTS.strokeWidth,
  minHeight = "240px",
  height,
  width = "100%",
  ...spinnerProps
}: SpinnerPageProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: height ? undefined : minHeight,
        height,
        width,
      }}
    >
      <Spinner size={size} strokeWidth={strokeWidth} {...spinnerProps} />
    </div>
  );
}
